import type { DurationObjectUnits, DurationUnit } from 'luxon';
import { DateTime, Duration, Interval, Settings } from 'luxon';

// years+months round down, months+days round down, days+hours round up, hours+minutes round up, minutes+seconds round up, seconds round up

// 1 year switch to years, 90 days (switch to months), 24 hours (switch to days), 60 minutes (switch to hours), 60 seconds (switch to minutes

const allResolutions: Array<DurationUnit> = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
] as const;

const roundUpUnits: Set<DurationUnit> = new Set(['days', 'hours', 'minutes', 'seconds']);

const roundDownUnits: Set<DurationUnit> = new Set(['years', 'months']);

export interface NormalizeDurationProps {
  start?: DateTime<true>;
  end?: DateTime<true>;
  top?: number;
  zone?: string;
}

const removeZeroValues = (durationObject: DurationObjectUnits) =>
  Object.fromEntries(
    Object.entries(durationObject).filter(([, value]) => value > 0)
  ) as DurationObjectUnits;

const removeZeroValuesFromDuration = (duration: Duration) =>
  Duration.fromObject(removeZeroValues(duration.toObject()));

const getUnitsForTop = (breakdownUnits: Array<DurationUnit>, unit: DurationUnit, top?: number) => {
  const unitStart = allResolutions.indexOf(unit);
  return top === undefined ? breakdownUnits : allResolutions.slice(unitStart, unitStart + top);
};

const roundDuration = (duration: Duration) => {
  const durationObj = duration.toObject();
  const durationKeys = Object.keys(durationObj) as Array<keyof DurationObjectUnits>;
  const lastUnit = durationKeys[durationKeys.length - 1];

  if (roundUpUnits.has(lastUnit)) {
    durationObj[lastUnit] = Math.ceil(durationObj[lastUnit] ?? 0);
  } else if (roundDownUnits.has(lastUnit)) {
    durationObj[lastUnit] = Math.floor(durationObj[lastUnit] ?? 0);
  }

  // Normalize will bring rounded lower values up
  const roundedDuration = Duration.fromObject(durationObj).normalize().toObject();

  return Duration.fromObject(removeZeroValues(roundedDuration));
};

const shiftDurationToUnit = (
  duration: Duration,
  unit: DurationUnit,
  units: Array<DurationUnit>,
  top?: number
) => {
  const shiftedDuration = duration.shiftTo(...getUnitsForTop(units, unit, top));
  return removeZeroValuesFromDuration(roundDuration(shiftedDuration));
};

const roundInterval = (
  interval: Interval<true>,
  breakdownUnits: Array<DurationUnit>,
  unit: DurationUnit,
  top?: number
) =>
  roundDuration(
    interval.toDuration(getUnitsForTop(breakdownUnits, unit, top), {
      conversionAccuracy: 'longterm',
    })
  );

const normalize = (interval: Interval<true>, top?: number) => {
  // This will be used to calculate which breakpoint, not for output
  const breakdown = removeZeroValues(interval.toDuration(allResolutions).toObject());
  const breakdownUnits = Object.keys(breakdown) as Array<DurationUnit>;

  if (breakdownUnits.length === 0) {
    return Duration.fromMillis(0);
  }

  // TODO: Consider if we could somehow state machine this to cleanup the shift up logic

  // Years
  if (interval.toDuration('years').years >= 1) {
    return roundInterval(interval, breakdownUnits, 'years', top);
  }

  // Months
  const { days } = interval.toDuration('days');
  if (days >= 90) {
    const roundedDate = roundInterval(interval, breakdownUnits, 'months', top);

    if (roundedDate.as('years') >= 1) {
      return shiftDurationToUnit(roundedDate, 'years', breakdownUnits, top);
    }

    return roundedDate;
  }

  // Days
  if (days >= 1) {
    const roundedDate = roundInterval(interval, breakdownUnits, 'days', top);

    if (roundedDate.as('days') >= 90) {
      return shiftDurationToUnit(roundedDate, 'months', breakdownUnits, top);
    }

    return roundedDate;
  }

  // Hours
  const { minutes } = interval.toDuration('minutes');
  if (minutes >= 90) {
    const roundedDate = roundInterval(interval, breakdownUnits, 'hours', top);

    if (roundedDate.as('days') >= 1) {
      return shiftDurationToUnit(roundedDate, 'days', breakdownUnits, top);
    }

    return roundedDate;
  }

  // Minutes
  if (minutes >= 1) {
    const roundedDate = roundInterval(interval, breakdownUnits, 'minutes', top);

    if (roundedDate.as('minutes') >= 90) {
      return shiftDurationToUnit(roundedDate, 'hours', breakdownUnits, top);
    }

    return roundedDate;
  }

  // Seconds
  const roundedDate = roundInterval(interval, breakdownUnits, 'seconds', top);

  if (roundedDate.as('minutes') >= 1) {
    return shiftDurationToUnit(roundedDate, 'minutes', breakdownUnits, top);
  }

  return roundedDate;
};

/**
 * @name normalizeDuration
 * @description
 * Get a Luxon duration from a start and end date, ready to be converted to a display format or used otherwise.
 * WARNING: use another function, such as `formatAndNormalizeDuration` for a human-readable string and error handling.
 * See: https://www.notion.so/teamhologram/Hologram-Writing-Guide-d85fbb8709e64fdc84374b76f937b566?pvs=4#c177d10d80d741d990c8fa761c6ba49e
 * And: https://www.notion.so/teamhologram/Hologram-Writing-Guide-d85fbb8709e64fdc84374b76f937b566?pvs=4#97171cc3105a4b089d564a717bc25bed
 * And for duration breakpoints/rounding: https://www.notion.so/teamhologram/SIMs-SIM-Inventory-section-5bb779c405814f6380c78391e527334a?pvs=4#2c32034018844bf4b8ead4d020c0fc2f
 *
 * Can be used to generate a `toHuman` string or a `toRelative` version, if the result is subtracted from `DateTime.now()`
 *
 * @param start Start date
 * @param end End date (defaults to today)
 * @param top Number of top units to display, defaults to all
 * @return User-friendly duration normalized and rounded according to rules above.
 * @throws Error if valid dates are passed but they couldn't be parsed
 */
const normalizeDuration = ({ start, end, top, zone = 'utc' }: NormalizeDurationProps) => {
  if (!start) {
    return undefined;
  }

  const oldDefaultZone = Settings.defaultZone;
  Settings.defaultZone = zone;

  const dtStart = start;
  const dtEnd = end ?? DateTime.now();

  if (dtStart.zone.name !== dtEnd.zone.name) {
    Settings.defaultZone = oldDefaultZone;
    throw new Error(
      `Timezones must match: Start zone '${dtStart.zone.name}' and end zone '${dtEnd.zone.name}' do not match.'`
    );
  }

  const interval = Interval.fromDateTimes(dtStart, dtEnd);

  if (!interval.isValid) {
    Settings.defaultZone = oldDefaultZone;
    throw new Error('Invalid interval');
  }

  const duration = normalize(interval, top);
  Settings.defaultZone = oldDefaultZone;
  return duration;
};

export default normalizeDuration;
